import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

import type { DocumentRow } from './documentLabels';
import { useDocumentTransition } from './useDocumentWrites';

interface RemoveDocumentDialogProps {
  /** The document to remove; kept while the dialog fades out. */
  document: DocumentRow | null;
  open: boolean;
  onCancel: () => void;
  /** After the backend has removed it, whatever opened the question closes too. */
  onRemoved?: () => void;
}

/**
 * Remove, asked before it is done (P13, PB8): the file is deleted and the
 * row stays, read-only, under the Removed status. The same question from the
 * row menu and from the drawer, so the wording cannot drift.
 */
export function RemoveDocumentDialog({
  document,
  open,
  onCancel,
  onRemoved,
}: RemoveDocumentDialogProps) {
  const transition = useDocumentTransition();

  return (
    <ConfirmDialog
      open={open}
      title={document ? `Remove “${document.title}”?` : 'Remove?'}
      description="Its file is deleted and it leaves the ARC. The row stays in the library under the Removed status and cannot be changed again."
      confirmLabel="Remove"
      destructive
      pending={transition.isPending}
      testId="documents-remove-dialog"
      onConfirm={() => {
        if (!document) return;
        transition.mutate(
          { row: document, to: 'ARCHIVED' },
          {
            onSuccess: () => onRemoved?.(),
            // The row was out of date either way; the question is over.
            onSettled: () => onCancel(),
          },
        );
      }}
      onCancel={onCancel}
    />
  );
}
